import React, { useEffect, useMemo, useState } from "react"
import api from "../../services/api"
import { toast } from "react-toastify"
import Loading from "../layouts/Loading"
import FormGroup from "../../components/Form/FormGroup"
import FormGroupInline from "../../components/Form/FormGroupInline"
import BreakForm from "../../components/Form/BreakForm"
import Select from "../../components/Form/Select"
import Input from "../../components/Form/Input"
import Button from "../../components/Form/Button"
import InfoTooltip from "../../components/InfoTooltip"
import { HiPencil } from "react-icons/hi"
import styles from "./governanceSettings.module.css"

const optionsBoolean = [
    { id: "1", value: "Sim" },
    { id: "0", value: "Não" },
]

const presetOptions = [
    { id: "commercial_balanced", value: "Equilibrado comercial" },
    { id: "strict_data_quality", value: "Controle rigoroso" },
    { id: "growth_cycles", value: "Crescimento e recorrência" },
    { id: "custom", value: "Customizado" },
]

const matchFieldOptions = [
    { id: "phone", value: "Telefone" },
    { id: "email", value: "E-mail" },
    { id: "document", value: "CPF/CNPJ" },
    { id: "name", value: "Nome" },
]

const duplicateScopeOptions = [
    { id: "account", value: "Toda a conta" },
    { id: "same_funnel", value: "Apenas no mesmo funil" },
]

const openActions = [
    { id: "reuse_existing", value: "Reaproveitar lead aberto" },
    { id: "warn", value: "Avisar e permitir novo lead" },
    { id: "block", value: "Bloquear novo lead" },
    { id: "allow", value: "Permitir sem alerta" },
]

const wonActions = [
    { id: "allow_new_cycle", value: "Permitir novo ciclo comercial" },
    { id: "reuse_existing", value: "Reaproveitar lead vendido" },
    { id: "warn", value: "Avisar e permitir novo ciclo" },
    { id: "block", value: "Bloquear novo lead" },
]

const lostActions = [
    { id: "reuse_existing", value: "Reabrir lead descartado" },
    { id: "allow_new_cycle", value: "Permitir novo ciclo comercial" },
    { id: "warn", value: "Avisar e permitir novo lead" },
    { id: "block", value: "Bloquear novo lead" },
]

const deletedActions = [
    { id: "warn", value: "Avisar e permitir" },
    { id: "block", value: "Bloquear novo lead" },
    { id: "allow", value: "Permitir sem alerta" },
]

const crossTeamActions = [
    { id: "use_lifecycle", value: "Usar regra do ciclo de vida" },
    { id: "allow_new_cycle", value: "Permitir novo lead em outra equipe" },
    { id: "warn", value: "Avisar e permitir" },
    { id: "reuse_existing", value: "Reaproveitar cadastro existente" },
    { id: "block", value: "Bloquear novo lead" },
]

const attributionOptions = [
    { id: "last_touch", value: "Último toque" },
    { id: "first_touch", value: "Primeiro toque" },
    { id: "linear", value: "Linear" },
]

const defaultPolicy = {
    preset: "commercial_balanced",
    identity: {
        is_active: true,
        use_contact_identity: true,
        duplicate_scope: "account",
        match_fields: ["phone", "email", "document"],
        confidence_threshold: "exact",
        allow_name_only_match: false,
    },
    presentation: {
        recent_indicator_minutes: 60,
    },
    lifecycle: {
        open: { action: "reuse_existing" },
        won: { action: "allow_new_cycle" },
        lost: { action: "reuse_existing" },
        deleted: { action: "warn" },
        cross_team_duplicate: { action: "use_lifecycle" },
    },
    attribution: {
        model: "last_touch",
        preserve_first_touch: true,
        record_reactivation_touch: true,
    },
    roi: {
        include_ads_cost: true,
        variable_cost_rate: 0,
        fixed_monthly_cost: 0,
    },
    alerts: {
        duplicates: {
            is_active: true,
            high_priority_min: 1,
            queue_accumulated_min: 10,
            min_resolution_rate: 50,
        },
        performance: {
            is_active: true,
            min_roas: 1,
            max_cpl: 0,
            min_conversion_rate: 0,
            spend_without_sale_min: 100,
        },
    },
    audit: {
        require_reason_for_policy_changes: false,
        require_reason_for_merge: false,
        keep_policy_change_history: true,
    },
}

const presetDescriptions = {
    commercial_balanced: "Boa política padrão: reaproveita leads em atendimento, permite recorrência depois de venda e reabre descartados.",
    strict_data_quality: "Mais segura para bases sensíveis: bloqueia cadastros repetidos e força mais controle operacional.",
    growth_cycles: "Mais flexível para aquisição: permite novos ciclos e usa alertas para revisão posterior.",
    custom: "Ajuste fino manual para a operação.",
}

const buildPresetPolicy = (preset) => {
    const base = JSON.parse(JSON.stringify(defaultPolicy))
    base.preset = preset

    if (preset === "strict_data_quality") {
        base.lifecycle.open.action = "block"
        base.lifecycle.won.action = "block"
        base.lifecycle.lost.action = "block"
        base.lifecycle.deleted.action = "block"
        base.audit.require_reason_for_merge = true
    }

    if (preset === "growth_cycles") {
        base.lifecycle.open.action = "warn"
        base.lifecycle.won.action = "allow_new_cycle"
        base.lifecycle.lost.action = "allow_new_cycle"
        base.lifecycle.deleted.action = "warn"
    }

    if (preset === "custom") {
        return null
    }

    return base
}

const GovernanceSettings = ({ account_id }) => {
    const [policy, setPolicy] = useState(defaultPolicy)
    const [tempPolicy, setTempPolicy] = useState(defaultPolicy)
    const [additionalFields, setAdditionalFields] = useState([])
    const [editable, setEditable] = useState(false)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (account_id) {
            getPolicy()
            getAdditionalFields()
        }
    }, [account_id])

    const fieldsOptions = useMemo(() => [
        ...matchFieldOptions,
        ...additionalFields.map(field => ({ id: `additional:${field._id}`, value: `Campo adicional: ${field.name}` })),
    ], [additionalFields])

    const riskLevel = useMemo(() => {
        const actions = [
            tempPolicy.lifecycle?.open?.action,
            tempPolicy.lifecycle?.won?.action,
            tempPolicy.lifecycle?.lost?.action,
            tempPolicy.lifecycle?.deleted?.action,
        ]
        if (!tempPolicy.identity?.is_active) return { label: "Alto risco", tone: "#b42318", description: "A política não está conferindo duplicidades antes de criar leads." }
        if (actions.includes("allow")) return { label: "Atenção", tone: "#b54708", description: "Existem cenários em que novos leads repetidos entram sem alerta." }
        if (actions.includes("block")) return { label: "Controle alto", tone: "#027a48", description: "A política prioriza qualidade da base, com maior chance de bloquear entradas." }
        return { label: "Equilibrada", tone: "#175cd3", description: "A política combina reaproveitamento, novos ciclos e alertas operacionais." }
    }, [tempPolicy])

    const getPolicy = () => {
        setLoading(true)
        api.get(`/accounts/${account_id}/crm/governance-policy`).then(response => {
            const data = response?.data?.data || defaultPolicy
            setPolicy(data)
            setTempPolicy(data)
        }).catch(err => toast.error(err?.response?.data?.message)).finally(() => setLoading(false))
    }

    const getAdditionalFields = () => {
        api.get("/additional-fields").then(response => {
            setAdditionalFields(response?.data?.data || [])
        }).catch(() => setAdditionalFields([]))
    }

    const setNested = (section, value) => {
        setTempPolicy(previous => ({
            ...previous,
            preset: previous.preset === "custom" || section === "preset" ? previous.preset : "custom",
            [section]: value,
        }))
    }

    const handlePreset = (preset) => {
        const presetPolicy = buildPresetPolicy(preset)
        setTempPolicy(previous => presetPolicy || { ...previous, preset: "custom" })
    }

    const handleIdentity = (name, value) => {
        setNested("identity", { ...(tempPolicy.identity || {}), [name]: value })
    }

    const handleLifecycle = (name, action) => {
        setNested("lifecycle", {
            ...(tempPolicy.lifecycle || {}),
            [name]: { action },
        })
    }

    const handleAttribution = (name, value) => {
        setNested("attribution", { ...(tempPolicy.attribution || {}), [name]: value })
    }

    const handlePresentation = (name, value) => {
        setNested("presentation", { ...(tempPolicy.presentation || {}), [name]: value })
    }

    const handleRoi = (name, value) => {
        setNested("roi", { ...(tempPolicy.roi || {}), [name]: value })
    }

    const handleDuplicateAlert = (name, value) => {
        setNested("alerts", {
            ...(tempPolicy.alerts || {}),
            duplicates: { ...(tempPolicy.alerts?.duplicates || {}), [name]: value },
        })
    }

    const handlePerformanceAlert = (name, value) => {
        setNested("alerts", {
            ...(tempPolicy.alerts || {}),
            performance: { ...(tempPolicy.alerts?.performance || {}), [name]: value },
        })
    }

    const handleAudit = (name, value) => {
        setNested("audit", { ...(tempPolicy.audit || {}), [name]: value })
    }

    const handleCancel = () => {
        setTempPolicy(policy)
        setEditable(false)
    }

    const handleSave = (e) => {
        e.preventDefault()

        if (!tempPolicy.identity?.match_fields?.length) {
            return toast.error("Selecione ao menos um identificador para a política.")
        }
        if (Number(tempPolicy.roi?.variable_cost_rate) < 0 || Number(tempPolicy.roi?.variable_cost_rate) > 100) {
            return toast.error("O custo variável deve estar entre 0 e 100%.")
        }
        if (Number(tempPolicy.alerts?.performance?.min_conversion_rate) < 0 || Number(tempPolicy.alerts?.performance?.min_conversion_rate) > 100) {
            return toast.error("A conversão mínima deve estar entre 0 e 100%.")
        }
        if (Number(tempPolicy.presentation?.recent_indicator_minutes) < 0 || Number(tempPolicy.presentation?.recent_indicator_minutes) > 10080) {
            return toast.error("O indicador de chegada recente deve ficar entre 0 e 10.080 minutos.")
        }

        setLoading(true)
        api.patch(`/accounts/${account_id}/crm/governance-policy`, tempPolicy).then(response => {
            const data = response?.data?.data || tempPolicy
            setPolicy(data)
            setTempPolicy(data)
            setEditable(false)
            toast.success(response?.data?.message)
        }).catch(err => toast.error(err?.response?.data?.message)).finally(() => setLoading(false))
    }

    const renderLabel = (text, tooltip) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            {text}
            <InfoTooltip text={tooltip} preferredPosition="bottom" />
        </span>
    )

    return (
        <>
            <BreakForm
                title="Política de Governança"
                iconAction={<HiPencil />}
                actionVariant="secondary"
                handleAction={(e) => { e.preventDefault(); setEditable(previous => !previous) }}
                showAction={!editable}
            />

            <div className={styles.content}>
                <div className={styles.riskBox} style={{ "--risk-tone": riskLevel.tone }}>
                    <strong>{riskLevel.label}</strong>
                    <p>{riskLevel.description}</p>
                </div>

                <FormGroup>
                    <FormGroup labelText={renderLabel("Perfil da política", "Use um perfil pronto para acelerar configuração. Qualquer ajuste fino transforma a política em customizada.")}>
                        <Select name="Perfil" options={presetOptions} selected={tempPolicy.preset} onChange={handlePreset} multiple={false} disabled={!editable} />
                        <small className={styles.helperText}>{presetDescriptions[tempPolicy.preset]}</small>
                    </FormGroup>

                    <div className={styles.sectionBreak}>
                        <BreakForm title="Identidade e Duplicidade" showAction={false} />
                    </div>
                    <FormGroup labelText={renderLabel("Conferir duplicidades antes de criar lead", "Quando ativo, entradas do CRM, API e chat passam pela política de identificação.")}>
                        <Select name="Conferência" options={optionsBoolean} selected={tempPolicy.identity?.is_active ? "1" : "0"} onChange={(value) => handleIdentity("is_active", value === "1")} multiple={false} disabled={!editable} />
                    </FormGroup>
                    <FormGroup labelText={renderLabel("Onde identificar duplicidades", "No mesmo funil, o mesmo contato pode ter um lead independente em cada funil. Em toda a conta, qualquer funil pode gerar correspondência.")}>
                        <Select name="Escopo da duplicidade" options={duplicateScopeOptions} selected={tempPolicy.identity?.duplicate_scope || "account"} onChange={(value) => handleIdentity("duplicate_scope", value)} multiple={false} disabled={!editable} />
                    </FormGroup>
                    <FormGroup labelText={renderLabel("Usar identidade canônica do contato", "Consulta ContactIdentity e vínculos LeadContact, não apenas o snapshot do lead.")}>
                        <Select name="Identidade canônica" options={optionsBoolean} selected={tempPolicy.identity?.use_contact_identity ? "1" : "0"} onChange={(value) => handleIdentity("use_contact_identity", value === "1")} multiple={false} disabled={!editable} />
                    </FormGroup>
                    <FormGroup labelText={renderLabel("Identificadores de correspondência", "Telefone, e-mail e documento são identificadores fortes. Nome deve ser usado com cuidado.")}>
                        <Select name="Identificadores" options={fieldsOptions} selected={tempPolicy.identity?.match_fields || []} onChange={(value) => handleIdentity("match_fields", value)} multiple={true} disabled={!editable} />
                    </FormGroup>
                    <FormGroup labelText={renderLabel("Permitir duplicidade só por nome", "Desative para evitar falsos positivos com nomes comuns.")}>
                        <Select name="Nome isolado" options={optionsBoolean} selected={tempPolicy.identity?.allow_name_only_match ? "1" : "0"} onChange={(value) => handleIdentity("allow_name_only_match", value === "1")} multiple={false} disabled={!editable} />
                    </FormGroup>

                    <div className={styles.sectionBreak}>
                        <BreakForm title="Ciclo de Vida do Lead" showAction={false} />
                    </div>
                    <FormGroup labelText="Se já existir lead aberto">
                        <Select name="Lead aberto" options={openActions} selected={tempPolicy.lifecycle?.open?.action} onChange={(value) => handleLifecycle("open", value)} multiple={false} disabled={!editable} />
                    </FormGroup>
                    <FormGroup labelText="Se já existir lead vendido">
                        <Select name="Lead vendido" options={wonActions} selected={tempPolicy.lifecycle?.won?.action} onChange={(value) => handleLifecycle("won", value)} multiple={false} disabled={!editable} />
                    </FormGroup>
                    <FormGroup labelText="Se já existir lead descartado">
                        <Select name="Lead descartado" options={lostActions} selected={tempPolicy.lifecycle?.lost?.action} onChange={(value) => handleLifecycle("lost", value)} multiple={false} disabled={!editable} />
                    </FormGroup>
                    <FormGroup labelText="Se já existir lead na lixeira">
                        <Select name="Lead na lixeira" options={deletedActions} selected={tempPolicy.lifecycle?.deleted?.action} onChange={(value) => handleLifecycle("deleted", value)} multiple={false} disabled={!editable} />
                    </FormGroup>
                    <FormGroup labelText={renderLabel("Se o cadastro encontrado estiver em outra equipe", "Use 'permitir novo lead' quando cada equipe representa uma empresa, filial ou unidade independente. Use a regra do ciclo quando a base deve ser única para todas as equipes.")}>
                        <Select name="Duplicidade entre equipes" options={crossTeamActions} selected={tempPolicy.lifecycle?.cross_team_duplicate?.action || "use_lifecycle"} onChange={(value) => handleLifecycle("cross_team_duplicate", value)} multiple={false} disabled={!editable} />
                    </FormGroup>

                    <div className={styles.sectionBreak}>
                        <BreakForm title="Chegada ao Funil" showAction={false} />
                    </div>
                    <FormGroup labelText={renderLabel("Exibir como recente por quantos minutos", "Leads novos e leads reaproveitados sobem para o topo. O selo de chegada desaparece automaticamente depois deste período; use zero para desativá-lo.")}>
                        <Input type="number" min="0" max="10080" value={tempPolicy.presentation?.recent_indicator_minutes ?? 60} onChange={(e) => handlePresentation("recent_indicator_minutes", e.target.value)} disabled={!editable} />
                    </FormGroup>

                    <div className={styles.sectionBreak}>
                        <BreakForm title="Atribuição e ROI" showAction={false} />
                    </div>
                    <FormGroup labelText="Modelo de atribuição">
                        <Select name="Atribuição" options={attributionOptions} selected={tempPolicy.attribution?.model} onChange={(value) => handleAttribution("model", value)} multiple={false} disabled={!editable} />
                    </FormGroup>
                    <FormGroup labelText="Preservar primeira origem do cliente">
                        <Select name="Primeira origem" options={optionsBoolean} selected={tempPolicy.attribution?.preserve_first_touch ? "1" : "0"} onChange={(value) => handleAttribution("preserve_first_touch", value === "1")} multiple={false} disabled={!editable} />
                    </FormGroup>
                    <FormGroup labelText="Registrar reativações como novos toques">
                        <Select name="Reativação" options={optionsBoolean} selected={tempPolicy.attribution?.record_reactivation_touch ? "1" : "0"} onChange={(value) => handleAttribution("record_reactivation_touch", value === "1")} multiple={false} disabled={!editable} />
                    </FormGroup>
                    <FormGroup labelText="Considerar investimento em anúncios">
                        <Select name="Ads" options={optionsBoolean} selected={tempPolicy.roi?.include_ads_cost ? "1" : "0"} onChange={(value) => handleRoi("include_ads_cost", value === "1")} multiple={false} disabled={!editable} />
                    </FormGroup>
                    <FormGroup labelText="Custo médio sobre cada venda (%)">
                        <Input type="number" value={tempPolicy.roi?.variable_cost_rate ?? ""} onChange={(e) => handleRoi("variable_cost_rate", e.target.value)} disabled={!editable} />
                    </FormGroup>
                    <FormGroup labelText="Custo mensal da operação (R$)">
                        <Input type="number" value={tempPolicy.roi?.fixed_monthly_cost ?? ""} onChange={(e) => handleRoi("fixed_monthly_cost", e.target.value)} disabled={!editable} />
                    </FormGroup>

                    <div className={styles.sectionBreak}>
                        <BreakForm title="Alertas e Auditoria" showAction={false} />
                    </div>
                    <FormGroup labelText="Alertas de duplicidade">
                        <Select name="Alertas de duplicidade" options={optionsBoolean} selected={tempPolicy.alerts?.duplicates?.is_active ? "1" : "0"} onChange={(value) => handleDuplicateAlert("is_active", value === "1")} multiple={false} disabled={!editable} />
                    </FormGroup>
                    <FormGroup labelText="Fila de duplicidade: alerta a partir de">
                        <Input type="number" value={tempPolicy.alerts?.duplicates?.queue_accumulated_min ?? ""} onChange={(e) => handleDuplicateAlert("queue_accumulated_min", e.target.value)} disabled={!editable} />
                    </FormGroup>
                    <FormGroup labelText="Meta mínima de revisão da fila (%)">
                        <Input type="number" value={tempPolicy.alerts?.duplicates?.min_resolution_rate ?? ""} onChange={(e) => handleDuplicateAlert("min_resolution_rate", e.target.value)} disabled={!editable} />
                    </FormGroup>
                    <FormGroup labelText="Alertas de performance">
                        <Select name="Alertas de performance" options={optionsBoolean} selected={tempPolicy.alerts?.performance?.is_active ? "1" : "0"} onChange={(value) => handlePerformanceAlert("is_active", value === "1")} multiple={false} disabled={!editable} />
                    </FormGroup>
                    <FormGroup labelText="ROAS mínimo">
                        <Input type="number" value={tempPolicy.alerts?.performance?.min_roas ?? ""} onChange={(e) => handlePerformanceAlert("min_roas", e.target.value)} disabled={!editable} />
                    </FormGroup>
                    <FormGroup labelText="CPL máximo (R$)">
                        <Input type="number" value={tempPolicy.alerts?.performance?.max_cpl ?? ""} onChange={(e) => handlePerformanceAlert("max_cpl", e.target.value)} disabled={!editable} />
                    </FormGroup>
                    <FormGroup labelText="Conversão mínima (%)">
                        <Input type="number" value={tempPolicy.alerts?.performance?.min_conversion_rate ?? ""} onChange={(e) => handlePerformanceAlert("min_conversion_rate", e.target.value)} disabled={!editable} />
                    </FormGroup>
                    <FormGroup labelText="Exigir motivo ao mesclar duplicidades">
                        <Select name="Motivo de mesclagem" options={optionsBoolean} selected={tempPolicy.audit?.require_reason_for_merge ? "1" : "0"} onChange={(value) => handleAudit("require_reason_for_merge", value === "1")} multiple={false} disabled={!editable} />
                    </FormGroup>

                    {editable &&
                        <FormGroupInline>
                            <Button variant="primary" onClick={handleSave}>Salvar política</Button>
                            <Button variant="secondary" onClick={handleCancel}>Cancelar</Button>
                        </FormGroupInline>
                    }
                </FormGroup>
            </div>
            {loading && <Loading fullPage={true} />}
        </>
    )
}

export default GovernanceSettings
